import SuperFetch from 'library/helpers/superFetch';
import { put, select, takeLatest } from 'redux-saga/effects';
import { settingsActions } from 'reduxStore/settings/slice';
import { defaultSettingsActions } from './slice';

// ✅ Determine query params based on environment
const queryParams = process.env.NODE_ENV === 'production' ? { isInternal: false } : {};

// These loaders are dispatched both from the auth boot saga (already orgID-gated)
// and from the Loading-screen 30s retry (NOT gated). Until a workspace is in
// scope, selectedProfile.orgID is undefined and SuperFetch would build
// /v5/undefined/settings/… which the backend 404s. Guard here so no dispatch
// path can leak an undefined-org request; the early return mirrors the existing
// error path (loading flag resets, LoadingComplete stays false to retry later).
const selectOrgID = (state) => state.Auth?.selectedProfile?.orgID;

export function* loadOrgDefaultSettings() {
	try {
		const orgID = yield select(selectOrgID);
		if (!orgID) {
			yield put(defaultSettingsActions.loadOrgDefaultSettingsError('No orgID in current profile'));
			return;
		}
		const defaultSettings = yield SuperFetch.get('/settings/defaultSettings', {}, queryParams, orgID).then(
			(res) => {
				if (res.statusCode === 200) return res.data;
				else throw new Error(`DEFAULT SETTINGS ORGANIZATION: ${res.error[0]}`);
			}
		);

		yield put(defaultSettingsActions.loadOrgDefaultSettingsSuccess(defaultSettings));
	} catch (error) {
		yield put(defaultSettingsActions.loadOrgDefaultSettingsError(error.message));
	}
}

export function* loadPersonDefaultSettings() {
	try {
		const orgID = yield select(selectOrgID);
		if (!orgID) {
			yield put(defaultSettingsActions.loadPersonDefaultSettingsError('No orgID in current profile'));
			return;
		}
		const defaultSettings = yield SuperFetch.get('/settings/people/defaultSettings', {}, queryParams, orgID).then(
			(res) => {
				if (res.statusCode === 200) return res.data;
				else throw new Error(`DEFAULT SETTING PERSON: ${res.error[0]}`);
			}
		);

		yield put(defaultSettingsActions.loadPersonDefaultSettingsSuccess(defaultSettings));
	} catch (error) {
		yield put(defaultSettingsActions.loadPersonDefaultSettingsError(error.message));
	}
}

export function* loadGroupSettingsSaga() {
	try {
		const orgID = yield select(selectOrgID);
		if (!orgID) {
			yield put(defaultSettingsActions.loadGroupSettingsError('No orgID in current profile'));
			return;
		}
		const defaultGroups = yield SuperFetch.get('/settings/group', {}, queryParams, orgID).then((res) => {
			if (res.statusCode === 200) return res.data;
			else throw new Error(`DEFAULT SETTING GROUP: ${res.error[0]}`);
		});

		yield put(defaultSettingsActions.loadGroupSettingsSuccess(defaultGroups));
	} catch (error) {
		yield put(defaultSettingsActions.loadGroupSettingsError(error.message));
	}
}

export function* loadOrgDefaultSettingsSuccess() {
	// once the default settings are loaded, we can load the org settings
	yield put(settingsActions.loadOrgSettings());
}

export function* loadPersonDefaultSettingsSuccess() {
	// once the default settings are loaded, we can load the person settings
	yield put(settingsActions.loadPersonSettings());
}

// ? No need to put out the actions for the groups as settings Group are the same across both default Settings and settings

// The main export for the saga
export default function* settingsSagas() {
	yield takeLatest(defaultSettingsActions.loadOrgDefaultSettings, loadOrgDefaultSettings);
	yield takeLatest(defaultSettingsActions.loadPersonDefaultSettings, loadPersonDefaultSettings);
	yield takeLatest(defaultSettingsActions.loadOrgDefaultSettingsSuccess, loadOrgDefaultSettingsSuccess);
	yield takeLatest(defaultSettingsActions.loadPersonDefaultSettingsSuccess, loadPersonDefaultSettingsSuccess);
	yield takeLatest(defaultSettingsActions.loadGroupSettings, loadGroupSettingsSaga);
}
